import Blurhash from "@components/blurhash";
import { cn } from "@lib/cn";
import { LinkPopUp } from "@library/components";
import { DESIGN_SYSTEM_SHADOW } from "@library/constants";
import { hasBorder as hasBorderFn, useAnchorTrigger } from "@library/shared";
import { useState } from "react";
import type { CSSProperties } from "styled-components";
import { getBlurhashFromUrl } from "@/shared";
import { isGradient } from "@components/color-picker/lib/detect";
import { ImageShapeEnum } from "../../image";
import { type ITickerCtx, useTickerCtx } from "../ctx";
import { type ITickerItem, TickerItemImageFitEnum } from "../types";

interface IProps {
  item: ITickerItem;
  idx: number;
  isVisible: boolean;
}

export const TickerItem = ({ item, idx, isVisible }: IProps) => {
  const { handleAnchorClick } = useAnchorTrigger();
  const [loaded, setLoaded] = useState(false);
  const [linkPopUp, setLinkPopUp] = useState<string | undefined>("");
  const ctx = useTickerCtx();
  const {
    editorData,
    elementSettings: { tickerItemImageFit },
  } = ctx;

  const isLink = item.linkType === "hyperlink";
  const hash = getBlurhashFromUrl(item.src);

  const handleItemClick = () => {
    if (editorData) return;
    if (item.linkType === "hyperlink" && item.openInPopup && item.linkUrl) {
      let url = item.linkUrl;
      if (!item.linkUrl.startsWith("http")) {
        url = `https://${item.linkUrl}`;
      }
      setLinkPopUp(url);
      return;
    }
  };

  let imgSrc = item.src;
  if (item.variable && !item.variable.includes("{{")) {
    imgSrc = item.variable;
  }

  const isFitInside = tickerItemImageFit === TickerItemImageFitEnum.Fit;
  const ItemWrapper = isLink ? "a" : "div";

  return (
    <>
      <ItemWrapper
        className={cn(
          "ticker-item tw-relative tw-flex tw-h-full tw-items-center tw-justify-center tw-overflow-hidden tw-border-solid",
          {
            "tw-cursor-pointer": !!item.linkType && item.linkType !== "none",
          },
        )}
        onClick={item.anchorTrigger ? handleAnchorClick : handleItemClick}
        data-anchor-trigger={item.anchorTrigger}
        data-analytics-click-event={item.linkAnalyticsEvent}
        {...makeItemLinkAttrs(item)}
        style={makeItemStyle({ idx, ctx, isFitInside })}
      >
        {item.variable && (
          <div className="tw-absolute tw-bottom-0 tw-right-0 [&>div]:tw-translate-x-0 [&>div]:tw-translate-y-0">
            {editorData?.variablesLabelSlot}
          </div>
        )}

        <div
          className={cn("tw-relative tw-flex", {
            "tw-size-full": !isFitInside,
            "tw-max-h-full tw-max-w-full": isFitInside,
          })}
        >
          {item.overlay && (
            <div
              className="tw-absolute tw-left-0 tw-top-0 tw-size-full"
              style={{
                backgroundColor: item.overlayColor ?? "",
                opacity: (item.overlayOpacity ?? 0) / 100,
              }}
            />
          )}
          {isVisible && (
            <img
              src={imgSrc}
              alt={item.alt}
              role="presentation"
              onLoad={() => setLoaded(true)}
              className={cn({
                "tw-max-h-full tw-max-w-full tw-object-contain": isFitInside,
                "tw-size-full tw-object-cover": !isFitInside,
              })}
            />
          )}
          {hash && !loaded && <Blurhash blurhash={hash} />}
        </div>
      </ItemWrapper>
      {!!linkPopUp && (
        <LinkPopUp src={linkPopUp} closePopUp={() => setLinkPopUp("")} />
      )}
    </>
  );
};

const makeItemStyle = ({
  idx,
  ctx,
  isFitInside,
}: {
  idx: number;
  ctx: ITickerCtx;
  isFitInside: boolean;
}): CSSProperties => {
  const { shouldAnimate, isHorizontal, isVertical, elementSettings } = ctx;

  const {
    tickerItems,
    backgroundColor,
    borderWidth,
    borderColor,
    round,
    shape,
    tickerItemsSpacing,
    shadow: settingsShadow,
    tickerItemPadding,
  } = elementSettings;

  let hasSpacing = true;
  if (!shouldAnimate) {
    hasSpacing = tickerItems.length > 1 && idx !== tickerItems.length - 1;
  }

  let shadowStyle: string | undefined;
  if (settingsShadow) {
    const shadow =
      DESIGN_SYSTEM_SHADOW[settingsShadow as keyof typeof DESIGN_SYSTEM_SHADOW];
    if (shadow) {
      shadowStyle = shadow;
    }
  }

  return {
    boxShadow: shadowStyle,
    marginRight: hasSpacing && isHorizontal ? `${tickerItemsSpacing}px` : "0px",
    marginBottom: hasSpacing && isVertical ? `${tickerItemsSpacing}px` : "0px",
    ...(isGradient(backgroundColor)
      ? { backgroundColor: "transparent", backgroundImage: backgroundColor }
      : { backgroundColor: backgroundColor || "transparent" }),
    ...(isGradient(borderColor)
      ? { borderColor: "transparent", borderImage: `${borderColor} 1` }
      : { borderColor: borderColor || "transparent" }),
    borderWidth: hasBorderFn(elementSettings) ? `${borderWidth}px` : "0px",
    borderRadius: shape === ImageShapeEnum.Circle ? "99999px" : round,
    padding: `${isFitInside ? tickerItemPadding : 0}px`,
    // @ts-expect-error
    "--item-image-fit": isFitInside ? "contain" : "cover",
  };
};

const makeItemLinkAttrs = (item: ITickerItem) => {
  const isLink = item.linkType === "hyperlink" && item.linkUrl;
  if (!isLink || item.openInPopup) return {};
  return {
    target: "_blank",
    href: item.linkUrl?.startsWith("http")
      ? item.linkUrl
      : `https://${item.linkUrl}`,
  };
};
